import * as THREE from "three";
import type { AnimationState, Transform } from "@mucki/shared";
import { createTransformBuffer, type TransformBuffer } from "../systems/interpolation.js";
import { createNameplate, type Nameplate } from "../ui/nameplate.js";
import { catColorFor, createCatMesh, type CatMesh } from "./catMesh.js";

export interface RemotePlayer {
  applySnapshot(transform: Transform, time: number): void;
  setName(name: string): void;
  setAnimationState(state: AnimationState): void;
  update(renderTime: number, delta: number, camera: THREE.Camera, canvas: HTMLCanvasElement): void;
  dispose(): void;
}

const NAMEPLATE_HEIGHT = 1.6;

export function createRemotePlayer(
  scene: THREE.Scene,
  sessionId: string,
  name: string,
  initial: Transform,
): RemotePlayer {
  const cat: CatMesh = createCatMesh(catColorFor(sessionId));
  cat.group.position.set(initial.x, initial.y, initial.z);
  cat.group.rotation.y = initial.rotationY;
  scene.add(cat.group);

  const buffer: TransformBuffer = createTransformBuffer(initial);
  const nameplate: Nameplate = createNameplate(name);
  const labelPosition = new THREE.Vector3();

  return {
    applySnapshot: (transform, time) => buffer.push({ ...transform, time }),
    setName: (next) => nameplate.setText(next),
    setAnimationState: (state) => cat.setAnimationState(state),
    update: (renderTime, delta, camera, canvas) => {
      const transform = buffer.sample(renderTime);
      cat.group.position.set(transform.x, transform.y, transform.z);
      cat.group.rotation.y = transform.rotationY;
      cat.update(delta);

      labelPosition.set(transform.x, transform.y + NAMEPLATE_HEIGHT, transform.z);
      nameplate.update(labelPosition, camera, canvas);
    },
    dispose: () => {
      cat.dispose();
      nameplate.dispose();
    },
  };
}
